import { useEffect, useRef, useState } from "react";

// GitHub's own public REST API: unauthenticated, CORS-open, and rate limited
// to 60 requests an hour per visitor IP -- a ceiling one page view never
// approaches. The list is fetched lazily, only once the reader opens the
// starred view, so a default page load still costs a single request (the
// contributions one).
const ENDPOINT = "https://api.github.com/users";
// One page. Someone with more than this many stars gets their most recent 100,
// which is the sort the endpoint already returns.
const PER_PAGE = 100;

function normalize(payload) {
  if (!Array.isArray(payload)) return [];
  return payload
    .filter((repo) => repo && repo.full_name && repo.html_url)
    .map((repo) => ({
      id: repo.id,
      fullName: repo.full_name,
      name: repo.name,
      owner: repo.owner?.login ?? repo.full_name.split("/")[0],
      url: repo.html_url,
      description: repo.description || "",
      stars: repo.stargazers_count || 0,
      // The API returns null for a repo whose language GitHub cannot detect --
      // awesome-lists and roadmaps, mostly. They get their own cluster.
      language: repo.language || "",
    }));
}

/**
 * Fetches the repositories a user has starred, once, the first time `enabled`
 * turns true. Like useContributions it never substitutes placeholder data: a
 * failed or rate-limited request leaves the caller an honest empty state.
 */
export default function useStarred(username, enabled) {
  const [state, setState] = useState({ status: "idle", repos: [] });
  const fetchedRef = useRef(null);

  useEffect(() => {
    if (!enabled || fetchedRef.current === username) return undefined;
    fetchedRef.current = username;

    const controller = new AbortController();
    let cancelled = false;

    setState({ status: "loading", repos: [] });

    fetch(
      `${ENDPOINT}/${encodeURIComponent(username)}/starred?per_page=${PER_PAGE}`,
      { signal: controller.signal, headers: { Accept: "application/vnd.github+json" } }
    )
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (cancelled) return;
        const repos = normalize(payload);
        if (repos.length === 0) throw new Error("no starred repositories");
        setState({ status: "ready", repos });
      })
      .catch((error) => {
        if (cancelled || error.name === "AbortError") return;
        // Let a later visit retry rather than caching the failure forever.
        fetchedRef.current = null;
        setState({ status: "error", repos: [] });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [username, enabled]);

  return state;
}
